/*
 * Lumao personalizer
 * - po nahrání fotky se AUTOMATICKY vygeneruje obrysový (contour) náhled přes AI proxy
 *   (Cloudflare Worker → Gemini); žádné tlačítko, AI je výchozí a jediný způsob náhledu
 * - výsledný obrys se vykreslí zlatě na tmavém panelu (engraved look) + volitelný zlatý text
 * - AI endpoint je chráněný Cloudflare Turnstile captchou (token se posílá s každým požadavkem)
 * - upload originálu přímo z prohlížeče na Cloudinary (unsigned preset), bez backendu
 * - URL fotky + texty + souhlas jdou do objednávky přes line item properties
 * - blokuje "Do košíku", dokud není fotka nahraná a souhlas potvrzený
 * - když AI selže (nebo není nastavená), spadne na záložní klientský náhled
 */
if (!customElements.get('lumao-personalizer')) {
  customElements.define(
    'lumao-personalizer',
    class LumaoPersonalizer extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.cloud = this.dataset.cloud;
        this.preset = this.dataset.preset;
        this.sectionId = this.dataset.sectionId;
        this.requirePhoto = this.dataset.requirePhoto === 'true';
        this.maxBytes = (parseFloat(this.dataset.maxMb) || 25) * 1024 * 1024;
        this.mode = this.dataset.mode || 'product'; // 'demo' = homepage náhled bez košíku/uploadu

        this.canvas = this.querySelector('[data-canvas]');
        this.ctx = this.canvas.getContext('2d');
        this.fileInput = this.querySelector('[data-file]');
        this.drop = this.querySelector('[data-drop]');
        this.dropText = this.querySelector('[data-droptext]');
        this.status = this.querySelector('[data-status]');
        this.urlInput = this.querySelector('[data-url]');
        this.text1 = this.querySelector('[data-text1]');
        this.text2 = this.querySelector('[data-text2]');
        this.consent = this.querySelector('[data-consent]');
        this.consentWrap = this.querySelector('[data-consent-wrap]');
        this.photoFlag = this.querySelector('[data-photo-flag]');
        this.gateMsg = this.querySelector('[data-gate]');
        this.detailInput = this.querySelector('[data-detail]');
        this.detailWrap = this.querySelector('[data-detailwrap]');
        this.aiUrl = this.dataset.aiUrl || '';
        this.tsSitekey = this.dataset.turnstileSitekey || '';
        this.loadingEl = this.querySelector('[data-loading]');
        this.loadingText = this.querySelector('[data-loadingtext]');
        this.tsEl = this.querySelector('[data-turnstile]');

        this.detail = this.detailInput ? parseInt(this.detailInput.value, 10) : 30;
        this.lineData = null; // { strength, w, h } z posledního náhledu
        this.lineCanvas = null; // hotová zlatá kresba (gold na průhledném)
        this._img = null; // poslední nahraná fotka (záložní náhled, když AI selže)

        this.bindEvents();
        this.ensureFontThenDraw();
        this.updateGate();
        // captchu si připrav dopředu (neviditelně), ať je token rychle po ruce
        if (this.tsSitekey) this.ensureTurnstile();
      }

      bindEvents() {
        if (this.fileInput) {
          this.fileInput.addEventListener('change', (e) => this.onFile(e.target.files[0]));
        }
        if (this.drop) {
          ['dragover', 'dragenter'].forEach((ev) =>
            this.drop.addEventListener(ev, (e) => {
              e.preventDefault();
              this.drop.classList.add('is-over');
            })
          );
          ['dragleave', 'drop'].forEach((ev) =>
            this.drop.addEventListener(ev, (e) => {
              e.preventDefault();
              this.drop.classList.remove('is-over');
            })
          );
          this.drop.addEventListener('drop', (e) => {
            const f = e.dataTransfer && e.dataTransfer.files[0];
            if (f) this.onFile(f);
          });
          this.drop.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.fileInput.click();
            }
          });
        }
        [this.text1, this.text2].forEach((el) => el && el.addEventListener('input', () => this.draw()));
        if (this.consent) this.consent.addEventListener('change', () => this.updateGate());
        if (this.detailInput) {
          this.detailInput.addEventListener('input', () => {
            this.detail = parseInt(this.detailInput.value, 10);
            this.renderLineCanvas();
            this.draw();
          });
        }
      }

      ensureFontThenDraw() {
        this.draw();
        if (document.fonts && document.fonts.load) {
          Promise.all([document.fonts.load('600 90px Caveat'), document.fonts.load('500 40px Poppins')])
            .then(() => this.draw())
            .catch(() => {});
          document.fonts.ready.then(() => this.draw()).catch(() => {});
        }
      }

      onFile(file) {
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
          this.setStatus('Nepodporovaný formát. Použij JPG, PNG nebo WEBP.', 'err');
          return;
        }
        if (file.size > this.maxBytes) {
          this.setStatus('Fotka je moc velká (max 25 MB).', 'err');
          return;
        }
        // máme fotku → výzva přes náhledem se smrskne na štítek „Změnit fotku" v rohu
        this.classList.add('has-photo');
        // vyčisti předchozí náhled, ať nezůstane viset stará kresba
        this.lineCanvas = null;
        this.lineData = null;
        this._img = null;
        if (this.detailWrap) this.detailWrap.hidden = true;
        this.draw();

        // dekóduj fotku do paměti (pro záložní náhled, fotka neopouští zařízení)
        const reader = new FileReader();
        reader.onload = (e) => {
          const im = new Image();
          im.onload = () => {
            this._img = im;
            // bez AI proxy uděláme aspoň klientský náhled
            if (!this.aiUrl) this.fallbackSketch();
          };
          im.src = e.target.result;
        };
        reader.readAsDataURL(file);

        if (this.aiUrl) {
          this.showLoading('Připravuji náhled…');
          if (this.dropText) this.dropText.textContent = 'Nahrávám…';
        }

        // nahraj originál na Cloudinary (potřeba pro objednávku i jako vstup pro AI)
        if (this.cloud && this.preset) {
          this.upload(file);
        } else if (this.mode !== 'demo') {
          this.setStatus('Úložiště fotek zatím není nastavené. Napiš nám prosím a fotku dořešíme.', 'err');
        }
      }

      async upload(file) {
        if (!this.cloud || !this.preset) return;
        this.uploading = true;
        if (this.urlInput) this.urlInput.value = '';
        this.updateGate();
        if (!this.aiUrl) this.setStatus('Nahrávám fotku…', 'load');
        try {
          const blob = await this.downscale(file);
          const fd = new FormData();
          if (blob === file) {
            fd.append('file', file);
          } else {
            fd.append('file', blob, (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg');
          }
          fd.append('upload_preset', this.preset);
          const res = await fetch(`https://api.cloudinary.com/v1_1/${this.cloud}/image/upload`, {
            method: 'POST',
            body: fd,
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const json = await res.json();
          if (!json.secure_url) throw new Error('Chybí secure_url');
          if (this.urlInput) this.urlInput.value = json.secure_url;
          if (this.dropText) this.dropText.textContent = 'Změnit fotku';
          if (this.aiUrl) {
            // AI obrys je výchozí: rovnou vygeneruj náhled
            this.generatePreview();
          } else {
            this.setStatus('Fotka nahraná ✓', 'ok');
          }
        } catch (err) {
          console.error('Lumao: nahrání na Cloudinary selhalo', err);
          this.hideLoading();
          this.setStatus('Nahrání se nepovedlo. Zkus to prosím znovu.', 'err');
          if (this.dropText) this.dropText.textContent = 'Zkusit znovu';
          // ať uživatel aspoň něco vidí
          this.fallbackSketch();
        } finally {
          this.uploading = false;
          this.updateGate();
        }
      }

      setStatus(msg, kind) {
        if (!this.status) return;
        this.status.textContent = msg;
        this.status.dataset.kind = kind || '';
      }

      /* ---------- převod fotky na perokresbu ---------- */

      processImage(img) {
        // zpracování v omezeném rozlišení (rychlost) při zachování poměru stran
        const MAXP = 600;
        const ir = img.width / img.height;
        let w;
        let h;
        if (ir >= 1) {
          w = MAXP;
          h = Math.max(1, Math.round(MAXP / ir));
        } else {
          h = MAXP;
          w = Math.max(1, Math.round(MAXP * ir));
        }
        const off = document.createElement('canvas');
        off.width = w;
        off.height = h;
        const o = off.getContext('2d');
        o.drawImage(img, 0, 0, w, h);
        const data = o.getImageData(0, 0, w, h).data;
        const n = w * h;

        const gray = new Float32Array(n);
        const inv = new Float32Array(n);
        for (let i = 0, p = 0; i < n; i++, p += 4) {
          const g = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
          gray[i] = g;
          inv[i] = 255 - g;
        }
        // sketch (color-dodge): čisté souvislé linky místo šumu jako u Canny
        const r = Math.max(2, Math.round(Math.min(w, h) * 0.018));
        const blurred = this.boxBlur(inv, w, h, r, 2);
        const strength = new Uint8ClampedArray(n);
        for (let i = 0; i < n; i++) {
          const denom = 255 - blurred[i];
          const d = denom <= 0 ? 255 : Math.min(255, (gray[i] * 255) / denom);
          strength[i] = 255 - d; // síla linky (0 = nic, 255 = silná linka)
        }
        this.lineData = { strength, w, h };
      }

      boxBlur(src, w, h, r, passes) {
        let a = src;
        for (let p = 0; p < passes; p++) {
          a = this.boxBlurH(a, w, h, r);
          a = this.boxBlurV(a, w, h, r);
        }
        return a;
      }

      boxBlurH(src, w, h, r) {
        const out = new Float32Array(w * h);
        const win = 2 * r + 1;
        for (let y = 0; y < h; y++) {
          const row = y * w;
          let sum = 0;
          for (let k = -r; k <= r; k++) sum += src[row + Math.min(w - 1, Math.max(0, k))];
          for (let x = 0; x < w; x++) {
            out[row + x] = sum / win;
            const add = src[row + Math.min(w - 1, x + r + 1)];
            const sub = src[row + Math.max(0, x - r)];
            sum += add - sub;
          }
        }
        return out;
      }

      boxBlurV(src, w, h, r) {
        const out = new Float32Array(w * h);
        const win = 2 * r + 1;
        for (let x = 0; x < w; x++) {
          let sum = 0;
          for (let k = -r; k <= r; k++) sum += src[Math.min(h - 1, Math.max(0, k)) * w + x];
          for (let y = 0; y < h; y++) {
            out[y * w + x] = sum / win;
            const add = src[Math.min(h - 1, y + r + 1) * w + x];
            const sub = src[Math.max(0, y - r) * w + x];
            sum += add - sub;
          }
        }
        return out;
      }

      renderLineCanvas() {
        if (!this.lineData) return;
        const { strength, w, h } = this.lineData;
        const s = this.detail != null ? this.detail : 30;
        const thr = 0.05 + 0.4 * ((100 - s) / 100); // vyšší slider = nižší práh = víc linek
        const span = Math.max(0.01, 1 - thr);
        // lehké defaultní zesílení obrysu: 1px dilatace smíchaná s originálem (0 = vyp, 1 = plné)
        const lines = this.boldenLines(strength, w, h, 0.6);
        const c = this.lineCanvas || (this.lineCanvas = document.createElement('canvas'));
        c.width = w;
        c.height = h;
        const cx = c.getContext('2d');
        const id = cx.createImageData(w, h);
        const d = id.data;
        const gr = 255;
        const gg = 216;
        const gb = 120;
        for (let i = 0, p = 0; i < w * h; i++, p += 4) {
          const l = lines[i] / 255;
          let a = l > thr ? Math.min(1, ((l - thr) / span) * 1.7) : 0;
          a = Math.pow(a, 0.8);
          d[p] = gr;
          d[p + 1] = gg;
          d[p + 2] = gb;
          d[p + 3] = Math.round(a * 255);
        }
        cx.putImageData(id, 0, 0);
      }

      // Lehce zesílí linky: separovatelný max-filtr (radius 1) zahuští obrys ~o 1px,
      // amount jen z části přimíchá dilataci k originálu, aby zesílení bylo jemné.
      boldenLines(src, w, h, amount) {
        if (!amount) return src;
        const tmp = new Float32Array(w * h);
        for (let y = 0; y < h; y++) {
          const row = y * w;
          for (let x = 0; x < w; x++) {
            const a = x > 0 ? src[row + x - 1] : 0;
            const b = src[row + x];
            const cc = x < w - 1 ? src[row + x + 1] : 0;
            tmp[row + x] = Math.max(a, b, cc);
          }
        }
        const out = new Uint8ClampedArray(w * h);
        for (let x = 0; x < w; x++) {
          for (let y = 0; y < h; y++) {
            const i = y * w + x;
            const a = y > 0 ? tmp[i - w] : 0;
            const b = tmp[i];
            const cc = y < h - 1 ? tmp[i + w] : 0;
            const dmax = Math.max(a, b, cc);
            out[i] = src[i] + (dmax - src[i]) * amount;
          }
        }
        return out;
      }

      /* ---------- náhled ---------- */

      rr(ctx, x, y, w, h, r) {
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, y, w, h, r);
          return;
        }
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      }

      draw() {
        const ctx = this.ctx;
        const W = this.canvas.width;
        const H = this.canvas.height;
        ctx.clearRect(0, 0, W, H);

        // proporční geometrie panelu + dřevěné základny
        const px = W * 0.1;
        const pw = W * 0.8;
        const py = H * 0.05;
        const ph = H * 0.76;
        const r = W * 0.045;
        const bw = pw * 0.6;
        const bh = H * 0.085;
        const bx = (W - bw) / 2;
        const by = py + ph - H * 0.008;

        // jemné zázemí scény
        const bg = ctx.createRadialGradient(W / 2, py + ph * 0.45, 40, W / 2, py + ph * 0.45, W * 0.7);
        bg.addColorStop(0, 'rgba(28,42,58,0.06)');
        bg.addColorStop(1, 'rgba(28,42,58,0)');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, W, H);

        // panel s jantarovou září
        ctx.save();
        ctx.shadowColor = 'rgba(242,180,65,0.45)';
        ctx.shadowBlur = W * 0.06;
        this.rr(ctx, px, py, pw, ph, r);
        ctx.fillStyle = '#0f141b';
        ctx.fill();
        ctx.restore();

        // vnitřek panelu + obsah (oříznuto na panel)
        ctx.save();
        this.rr(ctx, px, py, pw, ph, r);
        ctx.clip();
        const inner = ctx.createLinearGradient(0, py, 0, py + ph);
        inner.addColorStop(0, '#131a23');
        inner.addColorStop(1, '#0b0f15');
        ctx.fillStyle = inner;
        ctx.fillRect(px, py, pw, ph);

        const hasText = this.hasText();
        if (this.lineCanvas) this.drawLineArt(px, py, pw, ph, hasText);
        if (hasText) this.drawText(px, py, pw, ph);
        // výzvu k nahrání nese HTML overlay nad canvasem; text v canvasu jen když upload není
        if (!this.lineCanvas && !hasText && !this.drop) this.drawPlaceholder(px, py, pw, ph);
        ctx.restore();

        // tenký zlatý edge-lit okraj
        this.rr(ctx, px + 2, py + 2, pw - 4, ph - 4, r - 2);
        ctx.lineWidth = W * 0.0035;
        ctx.strokeStyle = 'rgba(255,216,120,0.85)';
        ctx.stroke();

        this.drawWood(bx, by, bw, bh, W);
      }

      drawLineArt(px, py, pw, ph, hasText) {
        const lc = this.lineCanvas;
        // contain-fit: celá kresba se vejde, nic se neořízne
        const availW = pw * 0.84;
        const availH = ph * (hasText ? 0.66 : 0.84);
        const ir = lc.width / lc.height;
        let dw;
        let dh;
        if (ir > availW / availH) {
          dw = availW;
          dh = availW / ir;
        } else {
          dh = availH;
          dw = availH * ir;
        }
        const ox = px + (pw - dw) / 2;
        const oy = py + (hasText ? ph * 0.06 : (ph - dh) / 2);

        const ctx = this.ctx;
        const W = this.canvas.width;
        ctx.save();
        // měkká záře pod linkami
        ctx.shadowColor = 'rgba(255,205,110,0.85)';
        ctx.shadowBlur = W * 0.02;
        ctx.drawImage(lc, ox, oy, dw, dh);
        ctx.shadowBlur = W * 0.045;
        ctx.globalAlpha = 0.45;
        ctx.drawImage(lc, ox, oy, dw, dh);
        ctx.restore();
      }

      hasText() {
        const t1 = ((this.text1 && this.text1.value) || '').trim();
        const t2 = ((this.text2 && this.text2.value) || '').trim();
        return !!(t1 || t2);
      }

      fitFont(ctx, text, maxW, base, weight, family, script) {
        let fs = base;
        const fam = `${weight} {SIZE}px "${family}", ${script ? 'cursive' : 'sans-serif'}`;
        ctx.font = fam.replace('{SIZE}', fs);
        while (ctx.measureText(text).width > maxW && fs > 12) {
          fs -= 2;
          ctx.font = fam.replace('{SIZE}', fs);
        }
        return fs;
      }

      drawText(px, py, pw, ph) {
        const ctx = this.ctx;
        const W = this.canvas.width;
        const t1 = ((this.text1 && this.text1.value) || '').trim();
        const t2 = ((this.text2 && this.text2.value) || '').trim();
        if (!t1 && !t2) return;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const cx = px + pw / 2;
        const y1 = this.lineCanvas ? py + ph * 0.8 : py + ph * 0.44;
        ctx.fillStyle = '#FFD879';
        ctx.shadowColor = 'rgba(255,200,90,0.9)';

        if (t1) {
          ctx.shadowBlur = W * 0.025;
          const fs = this.fitFont(ctx, t1, pw * 0.82, W * 0.12, '600', 'Caveat', true);
          ctx.font = `600 ${fs}px "Caveat", cursive`;
          ctx.fillText(t1, cx, y1);
        }
        if (t2) {
          ctx.shadowBlur = W * 0.016;
          const fs2 = this.fitFont(ctx, t2, pw * 0.84, W * 0.045, '500', 'Poppins', false);
          ctx.font = `500 ${fs2}px "Poppins", sans-serif`;
          ctx.fillText(t2, cx, y1 + (t1 ? W * 0.07 : 0));
        }
        ctx.restore();
      }

      drawPlaceholder(px, py, pw, ph) {
        const ctx = this.ctx;
        const W = this.canvas.width;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,216,120,0.4)';
        ctx.font = `500 ${W * 0.035}px "Poppins", sans-serif`;
        ctx.fillText('Tvůj náhled se zobrazí tady', px + pw / 2, py + ph / 2);
        ctx.restore();
      }

      drawWood(x, y, w, h, W) {
        const ctx = this.ctx;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.18)';
        ctx.shadowBlur = W * 0.02;
        ctx.shadowOffsetY = W * 0.01;
        this.rr(ctx, x, y, w, h, W * 0.02);
        const g = ctx.createLinearGradient(0, y, 0, y + h);
        g.addColorStop(0, '#e6c592');
        g.addColorStop(0.5, '#cda063');
        g.addColorStop(1, '#a9742f');
        ctx.fillStyle = g;
        ctx.fill();
        ctx.restore();
        ctx.save();
        this.rr(ctx, x, y, w, h, W * 0.02);
        ctx.clip();
        ctx.fillStyle = 'rgba(255,255,255,0.18)';
        ctx.fillRect(x, y, w, h * 0.08);
        ctx.restore();
      }

      /* zmenšení velkých fotek v prohlížeči před uploadem (kvalita pro gravuru + úspora kvóty) */
      downscale(file) {
        return new Promise((resolve) => {
          const MAX = 2400;
          const url = URL.createObjectURL(file);
          const im = new Image();
          im.onload = () => {
            const longest = Math.max(im.width, im.height);
            if (longest <= MAX && file.size <= 8 * 1024 * 1024) {
              URL.revokeObjectURL(url);
              resolve(file);
              return;
            }
            const scale = Math.min(1, MAX / longest);
            const c = document.createElement('canvas');
            c.width = Math.round(im.width * scale);
            c.height = Math.round(im.height * scale);
            c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
            c.toBlob(
              (b) => {
                URL.revokeObjectURL(url);
                resolve(b || file);
              },
              'image/jpeg',
              0.92
            );
          };
          im.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
          };
          im.src = url;
        });
      }

      /* ---------- loading stav nad náhledem ---------- */

      showLoading(msg) {
        if (this.loadingText && msg) this.loadingText.textContent = msg;
        if (this.loadingEl) this.loadingEl.hidden = false;
      }

      hideLoading() {
        if (this.loadingEl) this.loadingEl.hidden = true;
      }

      /* ---------- záložní klientský náhled (když AI selže / není nastavená) ---------- */

      fallbackSketch() {
        if (!this._img) return;
        this.processImage(this._img);
        this.renderLineCanvas();
        if (this.detailWrap) this.detailWrap.hidden = false;
        this.draw();
      }

      /* ---------- AI obrysový náhled (výchozí; přes proxy → Gemini) ---------- */

      async generatePreview() {
        if (!this.aiUrl || !this.urlInput || !this.urlInput.value) return;
        this.generating = true;
        this.showLoading('Vytvářím obrysový náhled…');
        this.setStatus('Vytvářím obrysový náhled…', 'load');
        try {
          const token = await this.getTurnstileToken();
          if (this.tsSitekey && !token) throw new Error('captcha');
          const res = await fetch(this.aiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageUrl: this.urlInput.value, turnstileToken: token }),
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          if (!data.output) throw new Error(data.error || 'no output');
          await this.loadAiLineArt(data.output);
          this.hideLoading();
          this.setStatus('Náhled hotový ✓', 'ok');
          if (this.detailWrap) this.detailWrap.hidden = false;
        } catch (err) {
          console.error('Lumao: AI náhled selhal', err);
          this.hideLoading();
          this.fallbackSketch();
          const msg =
            String(err && err.message) === 'captcha'
              ? 'Ověření proti robotům se nepovedlo. Načti prosím stránku znovu.'
              : 'Náhled se teď nepovedlo vytvořit. Zkus to prosím za chvíli znovu.';
          this.setStatus(msg, 'err');
        } finally {
          this.generating = false;
        }
      }

      /* ---------- Cloudflare Turnstile (captcha proti botům na AI endpointu) ---------- */

      ensureTurnstile() {
        if (!this.tsSitekey || !this.tsEl) return Promise.resolve(false);
        if (this._tsReady) return this._tsReady;
        this._tsReady = new Promise((resolve) => {
          const doRender = () => {
            try {
              // bez execution:execute → widget se spustí sám hned po renderu a token
              // přijde do callbacku; interaction-only = neviditelné, dokud netřeba challenge
              this.tsWidgetId = window.turnstile.render(this.tsEl, {
                sitekey: this.tsSitekey,
                appearance: 'interaction-only',
                callback: (token) => this._onToken(token),
                'error-callback': () => {
                  this._onToken('');
                  return true;
                },
                'expired-callback': () => {
                  this.tsToken = '';
                },
                'timeout-callback': () => {
                  this.tsToken = '';
                },
              });
              resolve(true);
            } catch (e) {
              resolve(false);
            }
          };
          if (window.turnstile && window.turnstile.render) {
            doRender();
            return;
          }
          const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          let s = document.querySelector('script[data-lumao-turnstile]');
          const onReady = () => (window.turnstile && window.turnstile.render ? doRender() : resolve(false));
          if (!s) {
            s = document.createElement('script');
            s.src = SRC;
            s.async = true;
            s.defer = true;
            s.setAttribute('data-lumao-turnstile', '');
            s.addEventListener('load', onReady);
            s.addEventListener('error', () => resolve(false));
            document.head.appendChild(s);
          } else if (window.turnstile && window.turnstile.render) {
            doRender();
          } else {
            s.addEventListener('load', onReady);
          }
        });
        return this._tsReady;
      }

      _onToken(token) {
        this.tsToken = token || '';
        const w = this._tsWaiter;
        this._tsWaiter = null;
        if (w) w(this.tsToken);
      }

      getTurnstileToken() {
        return this.ensureTurnstile().then((ok) => {
          if (!ok) return null;
          // token už máme z auto-renderu → použij ho a obnov widget pro příště
          if (this.tsToken) {
            const t = this.tsToken;
            this.tsToken = '';
            try {
              window.turnstile.reset(this.tsWidgetId);
            } catch (e) {
              /* widget se obnoví sám */
            }
            return t;
          }
          // ještě nedorazil → počkej na callback (s timeoutem)
          return new Promise((resolve) => {
            let done = false;
            this._tsWaiter = (t) => {
              if (!done) {
                done = true;
                resolve(t || null);
              }
            };
            setTimeout(() => {
              if (!done) {
                done = true;
                this._tsWaiter = null;
                resolve(this.tsToken || null);
              }
            }, 15000);
          });
        });
      }

      loadAiLineArt(src) {
        return new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => {
            this.processLineArtImage(im);
            this.renderLineCanvas();
            this.draw();
            resolve();
          };
          im.onerror = () => reject(new Error('AI obrázek se nenačetl'));
          im.src = src; // data URL z proxy → bez CORS problému
        });
      }

      // AI vrací tmavé linky na bílém → síla linky = tmavost
      processLineArtImage(img) {
        const MAXP = 700;
        const ir = img.width / img.height;
        let w;
        let h;
        if (ir >= 1) {
          w = MAXP;
          h = Math.max(1, Math.round(MAXP / ir));
        } else {
          h = MAXP;
          w = Math.max(1, Math.round(MAXP * ir));
        }
        const off = document.createElement('canvas');
        off.width = w;
        off.height = h;
        const o = off.getContext('2d');
        o.drawImage(img, 0, 0, w, h);
        const data = o.getImageData(0, 0, w, h).data;
        const n = w * h;
        const strength = new Uint8ClampedArray(n);
        const gray = new Float32Array(n);
        let sum = 0;
        for (let i = 0, p = 0; i < n; i++, p += 4) {
          const g = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
          gray[i] = g;
          sum += g;
        }
        // Auto-detekce polarity: lineart extraktor vrací BÍLÉ linky na ČERNÉM (nízký průměr)
        // → síla = jas; klasická perokresba ČERNÉ na BÍLÉM (vysoký průměr) → síla = 255 - jas.
        const whiteOnBlack = sum / n < 110;
        for (let i = 0; i < n; i++) {
          strength[i] = whiteOnBlack ? gray[i] : 255 - gray[i];
        }
        this.lineData = { strength, w, h };
      }

      /* ---------- blokování košíku ---------- */

      updateGate() {
        const submit = document.getElementById('ProductSubmitButton-' + this.sectionId);
        const reasons = [];
        const hasPhoto = !!(this.urlInput && this.urlInput.value);

        // Fotka se právě nahrává → počkej, ať se URL stihne připnout k objednávce.
        if (this.uploading) {
          reasons.push('Počkej prosím na dokončení nahrávání fotky…');
        } else if (this.requirePhoto && !hasPhoto) {
          reasons.push('Nahraj prosím fotku.');
        }

        // Souhlas řešíme JEN když je fotka nahraná — bez fotky není co potvrzovat.
        if (this.consent && hasPhoto && !this.consent.checked) {
          reasons.push('Potvrď prosím souhlas s použitím fotky.');
        }

        // Souhlas ukaž až ve chvíli, kdy fotka existuje (jinak mate prázdným checkboxem).
        if (this.consentWrap) this.consentWrap.hidden = !hasPhoto;

        // Lidsky čitelný příznak do objednávky: poznáš, u kterých objednávek čekáš fotku.
        if (this.photoFlag) {
          this.photoFlag.value = hasPhoto ? 'Nahráno' : 'Pošlu po objednání';
        }

        const gated = reasons.length > 0;
        if (submit) {
          submit.setAttribute('aria-disabled', gated ? 'true' : 'false');
          submit.classList.toggle('lumao-blocked', gated);
        }
        if (this.gateMsg) {
          this.gateMsg.hidden = !gated;
          this.gateMsg.textContent = reasons[0] || '';
        }
      }
    }
  );
}
